import * as React from 'react'
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui'

import { cn } from 'src/lib/utils'

import { OVERLAY_PANEL_CLASS } from './overlay-panel'

/* -------------------------------------------------------------------------
 * shadcn `AlertDialog` primitive. Backs the imperative confirm seam
 * (`Modal.confirm` / `Modal.info|success|error|warning`) and the MODAL shape of
 * the `Popconfirm` seam (`src/antd/Popconfirm/Popconfirm.shadcn.tsx`, whose
 * header says which acts get that shape rather than the anchored popover).
 *
 * AlertDialog rather than Dialog on purpose: antd's confirm has no dismissal
 * path that is neither OK nor Cancel — clicking the mask does nothing and the
 * dialog carries `role="alertdialog"`. Radix's AlertDialog is the primitive
 * with exactly those semantics (no outside-click close; Escape maps to the
 * cancel action, which is what antd's `keyboard` default does too).
 *
 * The panel scrolls for the same reason `./dialog.tsx` does: a centred fixed
 * box taller than the viewport is unreachable, and a confirm can carry a long
 * server message. That is one decision for both, so both read the panel class
 * from `./overlay-panel.ts`.
 * ------------------------------------------------------------------------- */

function AlertDialog({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
}

function AlertDialogPortal({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
  )
}

// `forwardRef` for the reason `./dialog.tsx` spells out: Radix's Portal hands
// each child a ref through a Slot and `Presence` needs the node to time the
// exit animation.
const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(function AlertDialogOverlay({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Overlay
      ref={ref}
      data-slot="alert-dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[1000] bg-black/45',
        className
      )}
      {...props}
    />
  )
})

function AlertDialogContent({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(OVERLAY_PANEL_CLASS, className)}
        {...props}
      />
    </AlertDialogPortal>
  )
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn('text-base leading-6 font-semibold', className)}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn('text-foreground text-sm', className)}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
}
